import type { FC, ReactNode } from 'react';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import SyntaxHighlighter from 'react-syntax-highlighter/dist/cjs/prism-light';
import bash from 'react-syntax-highlighter/dist/cjs/languages/prism/bash';
import go from 'react-syntax-highlighter/dist/cjs/languages/prism/go';
import java from 'react-syntax-highlighter/dist/cjs/languages/prism/java';
import javascript from 'react-syntax-highlighter/dist/cjs/languages/prism/javascript';
import json from 'react-syntax-highlighter/dist/cjs/languages/prism/json';
import python from 'react-syntax-highlighter/dist/cjs/languages/prism/python';
import ruby from 'react-syntax-highlighter/dist/cjs/languages/prism/ruby';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/cjs/styles/prism';

import styles from './ChatMarkdown.module.scss';
import { classifyLink } from './linkSafety';

// Register syntax highlighting languages
// This is done outside the component to avoid re-registering on each render
SyntaxHighlighter.registerLanguage('javascript', javascript);
SyntaxHighlighter.registerLanguage('json', json);
SyntaxHighlighter.registerLanguage('python', python);
SyntaxHighlighter.registerLanguage('go', go);
SyntaxHighlighter.registerLanguage('ruby', ruby);
SyntaxHighlighter.registerLanguage('java', java);
SyntaxHighlighter.registerLanguage('bash', bash);

type CodeProps = {
  className?: string;
  children: ReactNode;
  [key: string]: any;
};

// No `title`: it renders as a tooltip, so it is one more place for generated
// text to claim a destination the link does not have.
type AnchorProps = {
  href?: string;
  children: ReactNode;
};

type ImageProps = {
  src?: string;
  alt?: string;
};

// Outside the component so a streaming answer does not remount the tree on
// every chunk. Typed so a misspelled element name fails the build instead of
// silently falling back to ReactMarkdown's unvalidated default renderer.
const MarkdownComponents: Components = {
  a({ href, children }: AnchorProps) {
    const link = classifyLink(href);

    if (link.kind === 'unsafe') {
      return <>{children}</>;
    }

    const newTab = link.kind === 'annotated' || link.newTab;

    return (
      <a
        href={link.href}
        target={newTab ? '_blank' : undefined}
        rel={newTab ? 'noopener noreferrer' : undefined}
      >
        {children}
        {/* Inside the anchor so the real destination is part of the link's
            accessible name, not just visible next to it. */}
        {link.kind === 'annotated' && (
          <span className={styles.externalHost}> ({link.host})</span>
        )}
      </a>
    );
  },
  // An image is fetched with no reader action, so unlike a link there is no
  // moment at which a destination could be shown. Plaid-hosted or alt text.
  img({ src, alt }: ImageProps) {
    const image = classifyLink(src);

    if (image.kind !== 'trusted') {
      return <>{alt}</>;
    }

    return <img src={image.href} alt={alt ?? ''} />;
  },
  code({ className, children, ...props }: CodeProps) {
    const hasLang = /language-(\w+)/.exec(className || '');
    const content = String(children).replace(/\n$/, '');

    return hasLang ? (
      <SyntaxHighlighter
        style={vscDarkPlus}
        language={hasLang[1]}
        PreTag='div'
        className={styles.highlightedCode}
        showLineNumbers={true}
        wrapLines={true}
        useInlineStyles={true}
      >
        {content}
      </SyntaxHighlighter>
    ) : (
      <code className={className} {...props}>
        {content}
      </code>
    );
  },
};

const ChatMarkdown: FC<{ body: string }> = ({ body }) => {
  return (
    <div className={styles.chatMarkdown}>
      <ReactMarkdown
        components={MarkdownComponents}
        remarkPlugins={[remarkGfm]}
      >
        {body}
      </ReactMarkdown>
    </div>
  );
};

ChatMarkdown.displayName = 'ChatMarkdown';
export default ChatMarkdown;
